/**
 * Credentials Management Types
 * Hasonló az n8n credentials rendszeréhez
 */

import szamlazzLogo from '../assets/szamlazzhu_logo.svg';
import billingoLogo from '../assets/billingo_logo.svg';
import myposLogo from '../assets/mypos_logo.svg';
import stripeLogo from '../assets/stripe_logo.svg';
import seemeLogo from '../assets/seemelogo.png';

export type CredentialType = 
  | 'smtp'
  | 'openai'
  | 'claude'
  | 'gemini'
  | 'deepseek'
  | 'telegram_bot'
  | 'twilio'
  | 'seeme'
  | 'database'
  | 'http_auth'
  | 'oauth2'
  | 'api_key'
  | 'flintsign'
  | 'szamlazz'
  | 'billingo'
  | 'stripe'
  | 'mypos'
  | 'custom';

export interface Credential {
  id: string;
  name: string;
  type: CredentialType;
  description?: string;
  organizationId: string;
  data: Record<string, any>;  // Titkosítva tárolva
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  createdBy: string;
  usageCount?: number;  // Hány flow használja
}

// SMTP Credential
export interface SMTPCredentialData {
  host: string;
  port: number;
  secure: boolean;
  user: string;
  pass: string;  // Titkosítva
  from: {
    name: string;
    email: string;
  };
}

// OpenAI Credential
export interface OpenAICredentialData {
  apiKey: string;  // Titkosítva
  organization?: string;
  defaultModel?: string;
}

// Claude Credential
export interface ClaudeCredentialData {
  apiKey: string;  // Titkosítva
  defaultModel?: string;
}

// Gemini Credential
export interface GeminiCredentialData {
  apiKey: string;  // Titkosítva
  defaultModel?: string;
}

// DeepSeek Credential
export interface DeepSeekCredentialData {
  apiKey: string;  // Titkosítva
  baseUrl?: string;
}

// Telegram Bot Credential
export interface TelegramBotCredentialData {
  botToken: string;  // Titkosítva
  botUsername?: string;
}

// Twilio Credential
export interface TwilioCredentialData {
  accountSid: string;  // Titkosítva
  authToken: string;  // Titkosítva
  phoneNumber?: string;  // Twilio phone number (optional)
}

// Seeme.hu Credential
export interface SeemeCredentialData {
  apiKey: string;  // Seeme.hu API Key (titkosítva)
  senderId?: string;  // Feladó azonosító (opcionális, max 11 karakter)
}

// Database Credential
export interface DatabaseCredentialData {
  type: 'postgresql' | 'mysql' | 'mongodb' | 'firestore';
  host?: string;
  port?: number;
  database?: string;
  user?: string;
  password?: string;  // Titkosítva
  connectionString?: string;  // Titkosítva
}

// HTTP Auth Credential
export interface HTTPAuthCredentialData {
  authType: 'basic' | 'bearer' | 'api_key';
  username?: string;
  password?: string;  // Titkosítva
  token?: string;  // Titkosítva
  apiKey?: string;  // Titkosítva
  headerName?: string;
}

// OAuth2 Credential
export interface OAuth2CredentialData {
  clientId: string;
  clientSecret: string;  // Titkosítva
  accessToken?: string;  // Titkosítva
  refreshToken?: string;  // Titkosítva
  tokenType?: string;
  expiresAt?: string;
}

// FlintSign Credential
export interface FlintSignCredentialData {
  apiKey: string;  // Titkosítva
  certificateId?: string;
}

// Számlázz.hu Credential
export interface SzamlazzCredentialData {
  agentKey: string;  // Titkosítva - Számlázz.hu Agent kulcs
  username?: string;  // Számlázz.hu felhasználónév (opcionális)
  password?: string;  // Titkosítva - Számlázz.hu jelszó (opcionális)
}

// Billingo Credential
export interface BillingoCredentialData {
  apiKey: string;  // Titkosítva - Billingo API kulcs
  blockId?: string;  // Billingo Block ID (opcionális)
}

// API Key Credential
export interface APIKeyCredentialData {
  apiKey: string;  // Titkosítva
  headerName?: string;
  queryParamName?: string;
}

// Stripe Credential
export interface StripeCredentialData {
  apiKey: string;  // Titkosítva - Stripe Secret Key
  publishableKey?: string;  // Stripe Publishable Key (opcionális, frontend használatra)
  webhookSecret?: string;  // Titkosítva - Webhook signing secret (opcionális)
  mode?: 'test' | 'live';  // Test vagy éles mód
}

// MyPos Credential (2025 - New OAuth API)
export interface MyPosCredentialData {
  clientId: string;  // OAuth Client ID
  clientSecret: string;  // OAuth Client Secret (titkosítva)
  checkoutId?: string;  // Checkout ID (opcionális, ha van)
  webhookSecret?: string;  // Webhook Secret (HMAC verification)
  environment?: 'test' | 'production';  // Test vagy Production környezet
  
  // Legacy fields (deprecated - 2024 előtti API)
  sid?: string;  // Store ID (régi API)
  walletNumber?: string;  // Wallet Number (régi API)
  privateKey?: string;  // Private Key (régi API)
  publicCertificate?: string;  // Public Certificate (régi API)
  keyIndex?: number;  // Key Index (régi API)
}

// Credential típus metadata
export interface CredentialTypeMetadata {
  type: CredentialType;
  label: string;
  icon: string;
  logo?: string;  // SVG logo path (opcionális)
  description: string;
  category: 'communication' | 'ai' | 'database' | 'integration' | 'payment' | 'other';
  fields: CredentialField[];
}

export interface CredentialField {
  name: string;
  label: string;
  type: 'text' | 'password' | 'number' | 'select' | 'checkbox' | 'textarea';
  required: boolean;
  placeholder?: string;
  description?: string;
  options?: Array<{ value: string; label: string }>;
  defaultValue?: any;
}

// Credential típusok definíciói
export const CREDENTIAL_TYPES: Record<CredentialType, CredentialTypeMetadata> = {
  smtp: {
    type: 'smtp',
    label: 'SMTP Email',
    icon: '📧',
    description: 'Email küldéshez SMTP szerver',
    category: 'communication',
    fields: [
      { name: 'host', label: 'SMTP Host', type: 'text', required: true, placeholder: 'smtp.gmail.com' },
      { name: 'port', label: 'Port', type: 'number', required: true, defaultValue: 587 },
      { name: 'secure', label: 'SSL/TLS', type: 'checkbox', required: false, defaultValue: false },
      { name: 'user', label: 'Felhasználónév', type: 'text', required: true, placeholder: 'email@example.com' },
      { name: 'pass', label: 'Jelszó', type: 'password', required: true },
      { name: 'from.name', label: 'Feladó neve', type: 'text', required: true, placeholder: 'YouCRM' },
      { name: 'from.email', label: 'Feladó email', type: 'text', required: true, placeholder: 'noreply@youcrm.hu' }
    ]
  },
  openai: {
    type: 'openai',
    label: 'OpenAI',
    icon: '🤖',
    description: 'OpenAI API (ChatGPT, GPT-4)',
    category: 'ai',
    fields: [
      { name: 'apiKey', label: 'API Kulcs', type: 'password', required: true, placeholder: 'sk-...' },
      { name: 'organization', label: 'Organization ID', type: 'text', required: false, placeholder: 'org-...' },
      { name: 'defaultModel', label: 'Alapértelmezett modell', type: 'select', required: false, 
        options: [
          { value: 'gpt-3.5-turbo', label: 'GPT-3.5 Turbo' },
          { value: 'gpt-4', label: 'GPT-4' },
          { value: 'gpt-4-turbo', label: 'GPT-4 Turbo' }
        ]
      }
    ]
  },
  claude: {
    type: 'claude',
    label: 'Anthropic Claude',
    icon: '🧠',
    description: 'Anthropic Claude AI',
    category: 'ai',
    fields: [
      { name: 'apiKey', label: 'API Kulcs', type: 'password', required: true, placeholder: 'sk-ant-...' },
      { name: 'defaultModel', label: 'Alapértelmezett modell', type: 'select', required: false,
        options: [
          { value: 'claude-3-opus-20240229', label: 'Claude 3 Opus' },
          { value: 'claude-3-sonnet-20240229', label: 'Claude 3 Sonnet' },
          { value: 'claude-3-haiku-20240307', label: 'Claude 3 Haiku' }
        ]
      }
    ]
  },
  gemini: {
    type: 'gemini',
    label: 'Google Gemini',
    icon: '✨',
    description: 'Google Gemini AI',
    category: 'ai',
    fields: [
      { name: 'apiKey', label: 'API Kulcs', type: 'password', required: true },
      { name: 'defaultModel', label: 'Alapértelmezett modell', type: 'select', required: false,
        options: [
          { value: 'gemini-pro', label: 'Gemini Pro' },
          { value: 'gemini-pro-vision', label: 'Gemini Pro Vision' }
        ]
      }
    ]
  },
  deepseek: {
    type: 'deepseek',
    label: 'DeepSeek AI',
    icon: '🔍',
    description: 'DeepSeek AI API',
    category: 'ai',
    fields: [
      { name: 'apiKey', label: 'API Kulcs', type: 'password', required: true },
      { name: 'baseUrl', label: 'Base URL', type: 'text', required: false, placeholder: 'https://api.deepseek.com' }
    ]
  },
  telegram_bot: {
    type: 'telegram_bot',
    label: 'Telegram Bot',
    icon: '💬',
    description: 'Telegram Bot API',
    category: 'communication',
    fields: [
      { name: 'botToken', label: 'Bot Token', type: 'password', required: true, placeholder: '123456:ABC-DEF...' },
      { name: 'botUsername', label: 'Bot Username', type: 'text', required: false, placeholder: '@mybot' }
    ]
  },
  twilio: {
    type: 'twilio',
    label: 'Twilio SMS',
    icon: '📱',
    description: 'Twilio SMS API',
    category: 'communication',
    fields: [
      { name: 'accountSid', label: 'Account SID', type: 'text', required: true, placeholder: 'ACxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx' },
      { name: 'authToken', label: 'Auth Token', type: 'password', required: true, placeholder: 'your_auth_token' },
      { name: 'phoneNumber', label: 'Twilio Phone Number', type: 'text', required: false, placeholder: '+1234567890', description: 'Opcionális - flow-ban is megadható' }
    ]
  },
  seeme: {
    type: 'seeme',
    label: 'Seeme.hu SMS',
    icon: '💬',
    logo: seemeLogo,
    description: 'Seeme.hu SMS Gateway API',
    category: 'communication',
    fields: [
      { name: 'apiKey', label: 'API Key', type: 'password', required: true, placeholder: 'xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx', description: 'SMS Gateway beállítások oldalon található' },
      { name: 'senderId', label: 'Feladó azonosító', type: 'text', required: false, placeholder: 'YOUCRM', description: 'Opcionális - SMS feladó neve (max 11 karakter)' }
    ]
  },
  database: {
    type: 'database',
    label: 'Database',
    icon: '🗄️',
    description: 'Adatbázis kapcsolat',
    category: 'database',
    fields: [
      { name: 'type', label: 'Adatbázis típus', type: 'select', required: true,
        options: [
          { value: 'postgresql', label: 'PostgreSQL' },
          { value: 'mysql', label: 'MySQL' },
          { value: 'mongodb', label: 'MongoDB' },
          { value: 'firestore', label: 'Firestore' }
        ]
      },
      { name: 'host', label: 'Host', type: 'text', required: false, placeholder: 'localhost' },
      { name: 'port', label: 'Port', type: 'number', required: false },
      { name: 'database', label: 'Database', type: 'text', required: false },
      { name: 'user', label: 'Felhasználónév', type: 'text', required: false },
      { name: 'password', label: 'Jelszó', type: 'password', required: false },
      { name: 'connectionString', label: 'Connection String', type: 'textarea', required: false }
    ]
  },
  http_auth: {
    type: 'http_auth',
    label: 'HTTP Authentication',
    icon: '🔑',
    description: 'HTTP Basic/Bearer/API Key auth',
    category: 'integration',
    fields: [
      { name: 'authType', label: 'Auth típus', type: 'select', required: true,
        options: [
          { value: 'basic', label: 'Basic Auth' },
          { value: 'bearer', label: 'Bearer Token' },
          { value: 'api_key', label: 'API Key' }
        ]
      },
      { name: 'username', label: 'Felhasználónév', type: 'text', required: false },
      { name: 'password', label: 'Jelszó', type: 'password', required: false },
      { name: 'token', label: 'Token', type: 'password', required: false },
      { name: 'apiKey', label: 'API Key', type: 'password', required: false },
      { name: 'headerName', label: 'Header név', type: 'text', required: false, placeholder: 'X-API-Key' }
    ]
  },
  oauth2: {
    type: 'oauth2',
    label: 'OAuth2',
    icon: '🔐',
    description: 'OAuth2 authentication',
    category: 'integration',
    fields: [
      { name: 'clientId', label: 'Client ID', type: 'text', required: true },
      { name: 'clientSecret', label: 'Client Secret', type: 'password', required: true },
      { name: 'accessToken', label: 'Access Token', type: 'password', required: false },
      { name: 'refreshToken', label: 'Refresh Token', type: 'password', required: false }
    ]
  },
  flintsign: {
    type: 'flintsign',
    label: 'FlintSign',
    icon: '✍️',
    description: 'FlintSign digitális aláírás',
    category: 'integration',
    fields: [
      { name: 'apiKey', label: 'API Kulcs', type: 'password', required: true },
      { name: 'certificateId', label: 'Certificate ID', type: 'text', required: false }
    ]
  },
  szamlazz: {
    type: 'szamlazz',
    label: 'Számlázz.hu',
    icon: '🧾',
    logo: szamlazzLogo,
    description: 'Számlázz.hu számlázó rendszer',
    category: 'integration',
    fields: [
      { 
        name: 'agentKey', 
        label: 'Agent Kulcs', 
        type: 'password', 
        required: true, 
        placeholder: 'Számlázz.hu Agent kulcs',
        description: 'A Számlázz.hu Agent kulcs (https://docs.szamlazz.hu/hu/agent/basics/agent-key)'
      },
      { 
        name: 'username', 
        label: 'Felhasználónév', 
        type: 'text', 
        required: false, 
        placeholder: 'email@example.com',
        description: 'Számlázz.hu bejelentkezési email (opcionális)'
      },
      { 
        name: 'password', 
        label: 'Jelszó', 
        type: 'password', 
        required: false,
        description: 'Számlázz.hu jelszó (opcionális)'
      }
    ]
  },
  billingo: {
    type: 'billingo',
    label: 'Billingo',
    icon: '💰',
    logo: billingoLogo,
    description: 'Billingo számlázó rendszer',
    category: 'integration',
    fields: [
      { 
        name: 'apiKey', 
        label: 'API Kulcs', 
        type: 'password', 
        required: true, 
        placeholder: 'Billingo API kulcs',
        description: 'A Billingo API kulcs (https://www.billingo.hu/szolgaltatasok/api)'
      },
      { 
        name: 'blockId', 
        label: 'Block ID', 
        type: 'text', 
        required: false, 
        placeholder: 'Block ID',
        description: 'Billingo Block ID (opcionális)'
      }
    ]
  },
  api_key: {
    type: 'api_key',
    label: 'API Key',
    icon: '🔑',
    description: 'Általános API kulcs',
    category: 'integration',
    fields: [
      { name: 'apiKey', label: 'API Kulcs', type: 'password', required: true },
      { name: 'headerName', label: 'Header név', type: 'text', required: false, placeholder: 'X-API-Key' },
      { name: 'queryParamName', label: 'Query param név', type: 'text', required: false, placeholder: 'api_key' }
    ]
  },
  stripe: {
    type: 'stripe',
    label: 'Stripe Payment Gateway',
    icon: '💳',
    logo: stripeLogo,
    description: 'Stripe online fizetési átjáró - kártyás fizetések, előfizetések',
    category: 'payment',
    fields: [
      { 
        name: 'apiKey', 
        label: 'Secret Key', 
        type: 'password', 
        required: true, 
        placeholder: 'sk_test_... vagy sk_live_...',
        description: 'Stripe Secret API Key (Dashboard > Developers > API keys)'
      },
      { 
        name: 'publishableKey', 
        label: 'Publishable Key', 
        type: 'text', 
        required: false, 
        placeholder: 'pk_test_... vagy pk_live_...',
        description: 'Stripe Publishable Key (opcionális, frontend használatra)'
      },
      { 
        name: 'webhookSecret', 
        label: 'Webhook Secret', 
        type: 'password', 
        required: false, 
        placeholder: 'whsec_...',
        description: 'Webhook signing secret (opcionális, webhook események ellenőrzéséhez)'
      },
      { 
        name: 'mode', 
        label: 'Mód', 
        type: 'select', 
        required: false,
        defaultValue: 'test',
        options: [
          { value: 'test', label: 'Test (Teszt környezet)' },
          { value: 'live', label: 'Live (Éles környezet)' }
        ],
        description: 'Test vagy éles mód'
      }
    ]
  },
  mypos: {
    type: 'mypos',
    label: 'MyPOS Payment Gateway',
    icon: '💳',
    logo: myposLogo,
    description: 'MyPOS online fizetési átjáró - Támogatja az OAuth 2.0 REST API-t (2025) és a legacy IPC API-t',
    category: 'payment',
    fields: [
      // === ÚJ OAuth 2.0 REST API (2025) ===
      { 
        name: 'clientId', 
        label: 'Client ID (OAuth - új API)', 
        type: 'text', 
        required: false, 
        placeholder: 'qffqYB7kUYYbfbjqPyMR7WiH',
        description: 'OAuth Client ID - MyPOS Dashboard > Integration > REST API > Generate credentials'
      },
      { 
        name: 'clientSecret', 
        label: 'Client Secret (OAuth - új API)', 
        type: 'password', 
        required: false, 
        placeholder: 'bf4oDMejUX0uU5We8ULVTvY32NoPfnPdIDagqC2kj0gar5A8',
        description: 'OAuth Client Secret (titkosítva tárolva)'
      },
      { 
        name: 'webhookSecret', 
        label: 'Webhook Secret (OAuth - opcionális)', 
        type: 'password', 
        required: false,
        placeholder: 'whsec_xxxxx',
        description: 'Webhook Secret - HMAC signature verification-höz'
      },
      
      // === LEGACY IPC Purchase API (2024 előtti) ===
      { 
        name: 'sid', 
        label: 'SID / Store ID (Legacy IPC API)', 
        type: 'text', 
        required: false,
        placeholder: '000000000000010',
        description: 'Store ID - MyPOS Dashboard > Integration > Online Payments'
      },
      { 
        name: 'walletNumber', 
        label: 'Wallet Number (Legacy IPC API)', 
        type: 'text', 
        required: false,
        placeholder: '61938166610',
        description: 'Wallet Number - MyPOS Dashboard'
      },
      { 
        name: 'privateKey', 
        label: 'Private Key (Legacy IPC API)', 
        type: 'textarea', 
        required: false,
        placeholder: '-----BEGIN RSA PRIVATE KEY-----\n...\n-----END RSA PRIVATE KEY-----',
        description: 'RSA Private Key PEM formátumban (titkosítva tárolva)'
      },
      { 
        name: 'publicCertificate', 
        label: 'Public Certificate (Legacy IPC API - opcionális)', 
        type: 'textarea', 
        required: false,
        placeholder: '-----BEGIN CERTIFICATE-----\n...\n-----END CERTIFICATE-----',
        description: 'MyPOS Public Certificate - response signature verification-höz'
      },
      { 
        name: 'keyIndex', 
        label: 'Key Index (Legacy IPC API)', 
        type: 'number', 
        required: false,
        defaultValue: 1,
        placeholder: '1',
        description: 'Key Index - általában 1'
      },
      
      // === KÖZÖS ===
      { 
        name: 'environment', 
        label: 'Environment', 
        type: 'select', 
        required: false,
        defaultValue: 'test',
        options: [
          { value: 'test', label: 'Test (Sandbox)' },
          { value: 'production', label: 'Production (Live)' }
        ],
        description: 'Test (sandbox) vagy Production (live) környezet'
      }
    ]
  },
  custom: {
    type: 'custom',
    label: 'Egyéni',
    icon: '⚙️',
    description: 'Egyéni credential',
    category: 'other',
    fields: []
  }
};
